import React, { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Button, Tabs, message } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt } from '@fortawesome/pro-duotone-svg-icons';
import { H1 } from '../../shared-components/typography/Title';
import MainPage from '../../shared-components/main-page/MainPage';
import { notificationsAtom, pipelineHealthCurrentTabAtom, userPrivilagesAtom } from '../../../atoms/Atoms';
import { invalidatePipelineHealthCache } from '../../../api/pipelineHealthApi';
import ExecutiveOverview from './shared-components/ExecutiveOverview';
import DomainDeepDive from './shared-components/DomainDeepDive';
import VolumeAnalytics from './shared-components/VolumeAnalytics';
import SlaCompliance from './shared-components/SlaCompliance';
import ErrorAnalysis from './shared-components/ErrorAnalysis';
import SnapshotFiles from './shared-components/SnapshotFiles';

function PipelineHealth() {
  const { t } = useTranslation();
  const history = useHistory();
  const [currentTab, setCurrentTab] = useRecoilState(pipelineHealthCurrentTabAtom);
  const [notifications, setNotifications] = useRecoilState(notificationsAtom);
  const userPrivilages = useRecoilValue(userPrivilagesAtom);
  const [invalidating, setInvalidating] = useState(false);

  // The Customer-Support flag already covers Admin / CustomerSupport /
  // GlobalAdmin (see App.js line 133), which is the role set the user
  // asked for the cache-invalidate button to be gated on.
  const canInvalidateCache = !!userPrivilages.hasCustomerSupportPrivilages;

  const onInvalidateCache = () => {
    setInvalidating(true);
    invalidatePipelineHealthCache()
      .then((res) => {
        if (res?.data?.success === false) {
          setNotifications([
            ...notifications,
            { title: t('error'), description: res?.data?.error || t('error'), error: true }
          ]);
          return;
        }
        message.success(t('pipeline_health_cache_invalidated'));
      })
      .catch((err) => {
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: err?.response?.data?.message || err.message,
            error: true
          }
        ]);
      })
      .finally(() => setInvalidating(false));
  };

  // Access is gated to `PipelineHealth | Admin | GlobalAdmin`. The flag is
  // computed in App.js when the user loads. Until that effect has run the
  // privileges atom is an empty object — wait for a key to appear before
  // deciding to redirect.
  useEffect(() => {
    if (Object.keys(userPrivilages).length === 0) return;
    if (!userPrivilages.hasPipelineHealthPrivilages) {
      history.replace('/home');
    }
  }, [userPrivilages, history]);

  if (Object.keys(userPrivilages).length === 0) {
    return <MainPage><div /></MainPage>;
  }
  if (!userPrivilages.hasPipelineHealthPrivilages) {
    return null;
  }

  return (
    <MainPage>
      <div className="md:flex md:items-center">
        <H1 text={t('pipeline_health_dashboard')} />
        {canInvalidateCache && (
          <Button
            className="md:ml-auto"
            danger
            loading={invalidating}
            onClick={onInvalidateCache}
            icon={<FontAwesomeIcon icon={faTrashAlt} />}
          >
            {t('pipeline_health_invalidate_cache')}
          </Button>
        )}
      </div>
      <Tabs activeKey={currentTab} onChange={(key) => setCurrentTab(key)} className="h-full">
        <Tabs.TabPane key="1" tab={t('pipeline_health_tab_overview')}>
          <ExecutiveOverview />
        </Tabs.TabPane>
        <Tabs.TabPane key="2" tab={t('pipeline_health_tab_domain_deep_dive')}>
          <DomainDeepDive />
        </Tabs.TabPane>
        <Tabs.TabPane key="3" tab={t('pipeline_health_tab_volume')}>
          <VolumeAnalytics />
        </Tabs.TabPane>
        <Tabs.TabPane key="4" tab={t('pipeline_health_tab_sla')}>
          <SlaCompliance />
        </Tabs.TabPane>
        <Tabs.TabPane key="5" tab={t('pipeline_health_tab_errors')}>
          <ErrorAnalysis />
        </Tabs.TabPane>
        <Tabs.TabPane key="6" tab={t('pipeline_health_tab_snapshots')}>
          <SnapshotFiles />
        </Tabs.TabPane>
      </Tabs>
    </MainPage>
  );
}

export default PipelineHealth;
